import { apiBaseForHost, ulid } from '@delivery/shared';

/**
 * 接口基址。
 *
 * ★ 浏览器里走相对路径 `/api/v1` —— 由 vite / 反向代理转给后端,
 *   不跨域,也不用管部署在哪。
 *
 * ★ **打包成 App 之后没有代理**:页面是从本地文件加载的
 *   (iOS 上是 `capacitor://localhost`),相对路径会打到 App 自己身上,
 *   表现是每个接口都 404 而且看不出为什么。
 *   所以打包时用 `VITE_API_BASE` 写一个绝对地址,
 *   例如 `http://192.168.88.9:3000/api/v1`。
 */
/*
 * ★ 先看是从哪个域名打开的(freshvalley.nz / freshvalley.co.nz 各连各的后端,
 *   见 shared 里的 apiBaseForHost);认不出来(pages.dev、localhost、App)才用 VITE_API_BASE。
 */
export const BASE =
  apiBaseForHost(location.hostname) ??
  ((import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') || '/api/v1');

const TOKEN_KEY = 'delivery.driver.tokens';
const DEVICE_KEY = 'delivery.driver.device';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

/** 业务异常。司机端按 code 决定怎么处理,按 message 显示给司机 */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message);
    this.name = 'ApiFailure';
  }

  get code(): string {
    return this.error.code;
  }
}

export const tokens = {
  get(): Tokens | null {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  },
  set(t: Tokens): void {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(t));
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
  },
};

/** 设备 ID 持久化 —— 一账号一设备,换设备会踢掉旧会话 */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = ulid();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  // 并发请求同时 401 时只刷新一次,避免刷新风暴
  if (refreshing) return refreshing;
  const current = tokens.get();
  if (!current?.refreshToken) return false;

  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!res.ok) return false;
      tokens.set((await res.json()) as Tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** 401 时不尝试刷新(登录接口本身) */
  noRetry?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const doFetch = async (): Promise<Response> => {
    const t = tokens.get();
    return fetch(`${BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers: {
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(t ? { Authorization: `Bearer ${t.accessToken}` } : {}),
      },
      ...(opts.body ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  };

  let res = await doFetch();

  // access token 过期时静默刷新重试,司机不该被打断
  if (res.status === 401 && !opts.noRetry) {
    const body = (await res.clone().json().catch(() => null)) as { error?: ApiError } | null;
    if (body?.error?.code === 'TOKEN_EXPIRED' && (await tryRefresh())) {
      res = await doFetch();
    }
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: ApiError } | null;
    throw new ApiFailure(
      res.status,
      body?.error ?? { code: 'NETWORK_ERROR', message: '网络异常,请稍后重试' },
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * 给这台设备起个人认得出的名字。
 *
 * ★ 设备列表里只有一串随机 id 的话,「这台是不是我」没人答得上来 ——
 *   于是那一屏形同虚设:看到可疑设备也不敢踢。
 *   粗略认一下机型和浏览器就够用了,不做指纹。
 */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : '未知设备';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : '浏览器';
  return `${os} · ${browser}`;
}
